import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { PhotoCarousel } from './PhotoCarousel';
import {
    Home,
    Building2,
    MapPin,
    Euro,
    Maximize,
    BedDouble,
    DoorOpen,
    Plus,
    Star,
    Trash2,
    Edit,
    Loader2,
    Image,
    Sparkles,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const propertyTypes = [
    { value: 'apartment', label: 'Appartement', icon: Building2 },
    { value: 'house', label: 'Maison', icon: Home },
    { value: 'land', label: 'Terrain', icon: Maximize },
    { value: 'commercial', label: 'Commercial', icon: Building2 },
];

const featureOptions = [
    'Parking', 'Balcon', 'Terrasse', 'Jardin', 'Piscine', 
    'Cave', 'Ascenseur', 'Gardien', 'Digicode', 'Interphone',
    'Climatisation', 'Cheminée', 'Parquet', 'Double vitrage'
];

export const PropertyCard = ({ property, onEdit, onDelete, onToggleFeatured, isOwner = false }) => {
    const TypeIcon = propertyTypes.find(t => t.value === property.property_type)?.icon || Home;
    
    const formatPrice = (price) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR',
            maximumFractionDigits: 0
        }).format(price);
    };

    const defaultImage = 'https://images.unsplash.com/photo-1706808849827-7366c098b317?w=400&h=300&fit=crop';

    return (
        <div 
            className={`bg-white rounded-xl overflow-hidden border shadow-soft transition-shadow hover:shadow-md ${
                property.is_featured ? 'ring-2 ring-scalimmo-gold' : 'border-border/60'
            }`}
            data-testid={`property-card-${property.id}`}
        >
            {/* Image */}
            <div className="relative">
                <PhotoCarousel
                    photos={property.photos}
                    alt={property.title}
                    placeholder={defaultImage}
                    className="h-48"
                />
                {property.is_featured && (
                    <div className="absolute top-3 left-3 z-10">
                        <Badge className="bg-scalimmo-gold text-white gap-1">
                            <Star className="w-3 h-3 fill-current" />
                            Top 3
                        </Badge>
                    </div>
                )}
                <div className="absolute top-3 right-3 z-10">
                    <Badge variant="secondary" className="bg-white/90">
                        {propertyTypes.find(t => t.value === property.property_type)?.label}
                    </Badge>
                </div>
            </div>

            {/* Content */}
            <div className="p-4 space-y-3">
                <div>
                    <h3 className="font-heading text-lg font-semibold text-scalimmo-navy line-clamp-1">
                        {property.title}
                    </h3>
                    <div className="flex items-center gap-1 text-muted-foreground text-sm mt-1">
                        <MapPin className="w-3.5 h-3.5" />
                        {property.city}
                    </div>
                </div>

                {/* Price */}
                <p className="text-2xl font-heading font-semibold text-scalimmo-navy">
                    {formatPrice(property.price)}
                </p>

                {/* Details */}
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                        <Maximize className="w-4 h-4" />
                        {property.surface} m²
                    </span>
                    {property.rooms && (
                        <span className="flex items-center gap-1">
                            <DoorOpen className="w-4 h-4" />
                            {property.rooms} pièces
                        </span>
                    )}
                    {property.bedrooms && (
                        <span className="flex items-center gap-1">
                            <BedDouble className="w-4 h-4" />
                            {property.bedrooms} ch.
                        </span>
                    )}
                </div>

                {/* Features */}
                {property.features?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                        {property.features.slice(0, 3).map((feature, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                                {feature}
                            </Badge>
                        ))}
                        {property.features.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                                +{property.features.length - 3}
                            </Badge>
                        )}
                    </div>
                )}

                {/* Owner Actions */}
                {isOwner && (
                    <div className="flex items-center gap-2 pt-2 border-t border-border">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => onToggleFeatured?.(property)}
                            className={`flex-1 ${property.is_featured ? 'text-scalimmo-gold border-scalimmo-gold' : ''}`}
                            data-testid={`toggle-featured-${property.id}`}
                        >
                            <Star className={`w-4 h-4 mr-1 ${property.is_featured ? 'fill-current' : ''}`} />
                            {property.is_featured ? 'Top 3' : 'Mettre en avant'}
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onEdit?.(property)}
                        >
                            <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onDelete?.(property)}
                            className="text-destructive hover:text-destructive"
                        >
                            <Trash2 className="w-4 h-4" />
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};


export const PropertyForm = ({ property, onSubmit, onCancel }) => {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        title: property?.title || '',
        property_type: property?.property_type || 'apartment',
        price: property?.price || '',
        surface: property?.surface || '',
        rooms: property?.rooms || '',
        bedrooms: property?.bedrooms || '',
        city: property?.city || '',
        address: property?.address || '',
        description: property?.description || '',
        photos: property?.photos || [],
        features: property?.features || [],
        is_featured: property?.is_featured || false,
    });

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const toggleFeature = (feature) => {
        setFormData(prev => ({
            ...prev,
            features: prev.features.includes(feature)
                ? prev.features.filter(f => f !== feature)
                : [...prev.features, feature]
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            await onSubmit({
                ...formData,
                price: parseFloat(formData.price),
                surface: parseFloat(formData.surface),
                rooms: formData.rooms ? parseInt(formData.rooms) : null,
                bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
                <Label>Titre de l'annonce *</Label>
                <Input
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    placeholder="Ex: Bel appartement lumineux centre-ville"
                    required
                    data-testid="property-title"
                />
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label>Type de bien *</Label>
                    <Select
                        value={formData.property_type}
                        onValueChange={(v) => handleChange('property_type', v)}
                    >
                        <SelectTrigger data-testid="property-type">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {propertyTypes.map(type => (
                                <SelectItem key={type.value} value={type.value}>
                                    {type.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-2">
                    <Label>Prix (€) *</Label>
                    <Input
                        type="number"
                        value={formData.price}
                        onChange={(e) => handleChange('price', e.target.value)}
                        placeholder="250000"
                        required
                        data-testid="property-price"
                    />
                </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                    <Label>Surface (m²) *</Label>
                    <Input
                        type="number"
                        value={formData.surface}
                        onChange={(e) => handleChange('surface', e.target.value)}
                        placeholder="75"
                        required
                        data-testid="property-surface"
                    />
                </div>
                <div className="space-y-2">
                    <Label>Pièces</Label>
                    <Input
                        type="number"
                        value={formData.rooms}
                        onChange={(e) => handleChange('rooms', e.target.value)}
                        placeholder="4"
                    />
                </div>
                <div className="space-y-2">
                    <Label>Chambres</Label>
                    <Input
                        type="number"
                        value={formData.bedrooms}
                        onChange={(e) => handleChange('bedrooms', e.target.value)}
                        placeholder="2"
                    />
                </div>
            </div>

            <div className="space-y-2">
                <Label>Ville *</Label>
                <Input
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    placeholder="Paris"
                    required
                    data-testid="property-city"
                />
            </div>

            <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Décrivez le bien..."
                    rows={3}
                />
            </div>

            <div className="space-y-2">
                <Label>URL de la photo principale</Label>
                <Input
                    value={formData.photos[0] || ''}
                    onChange={(e) => handleChange('photos', e.target.value ? [e.target.value] : [])}
                    placeholder="https://..."
                    data-testid="property-photo"
                />
            </div>

            <div className="space-y-2">
                <Label>Caractéristiques</Label>
                <div className="flex flex-wrap gap-2">
                    {featureOptions.map(feature => (
                        <Badge
                            key={feature}
                            variant={formData.features.includes(feature) ? 'default' : 'outline'}
                            className={`cursor-pointer transition-colors ${
                                formData.features.includes(feature) 
                                    ? 'bg-scalimmo-navy' 
                                    : 'hover:bg-muted'
                            }`}
                            onClick={() => toggleFeature(feature)}
                        >
                            {feature}
                        </Badge>
                    ))}
                </div>
            </div>

            <div className="flex gap-3 pt-4">
                <Button
                    type="button"
                    variant="outline"
                    onClick={onCancel}
                    className="flex-1"
                >
                    Annuler
                </Button>
                <Button
                    type="submit"
                    className="flex-1 btn-primary"
                    disabled={loading}
                    data-testid="submit-property"
                >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (property ? 'Modifier' : 'Ajouter')}
                </Button>
            </div>
        </form>
    );
};


export const PropertiesSection = () => {
    const { user, token } = useAuth();
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingProperty, setEditingProperty] = useState(null);

    useEffect(() => {
        if (user?.user_type === 'agent') {
            fetchProperties();
        }
    }, [user]);

    const fetchProperties = async () => {
        try {
            const response = await axios.get(`${API}/properties/my`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setProperties(response.data);
        } catch (error) {
            console.error('Error fetching properties:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (data) => {
        try {
            await axios.post(`${API}/properties`, data, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Bien ajouté avec succès !');
            setShowForm(false);
            fetchProperties();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'ajout');
        }
    };

    const handleUpdate = async (data) => {
        try {
            await axios.put(`${API}/properties/${editingProperty.id}`, data, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Bien modifié avec succès !');
            setEditingProperty(null);
            fetchProperties();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
        }
    };

    const handleDelete = async (property) => {
        if (!confirm('Supprimer ce bien ?')) return;
        
        try {
            await axios.delete(`${API}/properties/${property.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Bien supprimé');
            fetchProperties();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleToggleFeatured = async (property) => {
        try {
            await axios.put(`${API}/properties/${property.id}/toggle-featured`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchProperties();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur');
        }
    };

    const featuredCount = properties.filter(p => p.is_featured).length;

    if (!user || user.user_type !== 'agent') return null;

    return (
        <div className="space-y-6" data-testid="properties-section">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="font-heading text-xl font-semibold text-scalimmo-navy flex items-center gap-2">
                        <Home className="w-5 h-5" />
                        Mes biens ({properties.length})
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        <Sparkles className="w-4 h-4 inline mr-1 text-scalimmo-gold" />
                        {featuredCount}/3 biens mis en avant sur votre profil
                    </p>
                </div>

                <Dialog open={showForm} onOpenChange={setShowForm}>
                    <DialogTrigger asChild>
                        <Button className="btn-primary" data-testid="add-property-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            Ajouter un bien
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="font-heading">Ajouter un bien</DialogTitle>
                            <DialogDescription>
                                Créez une nouvelle annonce immobilière
                            </DialogDescription>
                        </DialogHeader>
                        <PropertyForm
                            onSubmit={handleCreate}
                            onCancel={() => setShowForm(false)}
                        />
                    </DialogContent>
                </Dialog>
            </div>

            {/* Properties Grid */}
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-scalimmo-navy" />
                </div>
            ) : properties.length === 0 ? (
                <div className="bg-white rounded-xl border border-border/60 p-12 text-center shadow-soft">
                    <Home className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                    <h3 className="font-heading text-lg font-semibold text-scalimmo-navy mb-2">
                        Aucun bien
                    </h3>
                    <p className="text-muted-foreground mb-6">
                        Ajoutez vos biens pour les afficher sur votre profil
                    </p>
                    <Button className="btn-primary" onClick={() => setShowForm(true)}>
                        <Plus className="w-4 h-4 mr-2" />
                        Ajouter mon premier bien
                    </Button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {properties.map(property => (
                        <PropertyCard
                            key={property.id}
                            property={property}
                            isOwner={true}
                            onEdit={setEditingProperty}
                            onDelete={handleDelete}
                            onToggleFeatured={handleToggleFeatured}
                        />
                    ))}
                </div>
            )}

            {/* Edit Modal */}
            <Dialog open={!!editingProperty} onOpenChange={() => setEditingProperty(null)}>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Modifier le bien</DialogTitle>
                    </DialogHeader>
                    {editingProperty && (
                        <PropertyForm
                            property={editingProperty}
                            onSubmit={handleUpdate}
                            onCancel={() => setEditingProperty(null)}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};


// Component for Agent Profile Page (read-only display)
export const FeaturedProperties = ({ agentId }) => {
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchFeaturedProperties();
    }, [agentId]);

    const fetchFeaturedProperties = async () => {
        try {
            const response = await axios.get(`${API}/properties/agent/${agentId}/featured`);
            setProperties(response.data);
        } catch (error) {
            console.error('Error fetching featured properties:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-scalimmo-navy" />
            </div>
        );
    }

    if (properties.length === 0) {
        return null;
    }

    return (
        <div className="space-y-4" data-testid="featured-properties">
            <h2 className="font-heading text-2xl font-semibold text-scalimmo-navy flex items-center gap-2">
                <Star className="w-5 h-5 text-scalimmo-gold fill-scalimmo-gold" />
                Top 3 des biens
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {properties.map(property => (
                    <PropertyCard key={property.id} property={property} />
                ))}
            </div>
        </div>
    );
};
